import { rufe } from './client';

/**
 * Der Zugang zur Kundenakte (M-10, M-11, M-12).
 *
 * Die Typen spiegeln den Vertrag der API und nicht die Tabellen. Eine
 * Eigenheit zieht sich durch: Felder mit einer Berechtigung nach SYS-03 sind
 * OPTIONAL und FEHLEN, wenn der angemeldete Benutzer sie nicht sehen darf.
 * Sie sind nicht `null` — das Pflichtenheft ist in M-12 ausdruecklich: "Ein
 * graues Feld verraet, dass es einen Wert gibt." Die Oberflaeche prueft
 * deshalb mit `in` und nicht auf einen Wert.
 */

export type Kundenart = 'privat' | 'geschaeft' | 'oeffentlich';

/** Die drei Kundenarten in fester Reihenfolge — fuer Kaestchen und Listen. */
export const KUNDENARTEN: readonly Kundenart[] = ['privat', 'geschaeft', 'oeffentlich'];

/**
 * Wie die Kundenarten heissen, wenn ein Mensch sie liest.
 *
 * Die Zuordnung stand vorher in `Belegvorlagen.tsx` und waere beim zweiten
 * Bedarf ein zweites Mal abgeschrieben worden. Sie gehoert neben den Typ.
 */
export const KUNDENARTNAME: Readonly<Record<Kundenart, string>> = {
  privat: 'Privatkunden',
  geschaeft: 'Geschäftskunden',
  oeffentlich: 'öffentliche Auftraggeber',
};
export type Kundenstatus = 'interessent' | 'kunde' | 'inaktiv';

/**
 * PRD-38, KUN-13 (CRM-171): wie ein Kunde gekennzeichnet wird. Kostenträger ist
 * eine Rolle neben dem Status, kein Statuswert; die Regel steht in der
 * Datenbank (`fach.kunde_marke`), Liste, Akte und Suche zeigen sie gleich.
 */
export type Kundenmarke = Kundenstatus | 'kostentraeger';

/** Die Kennzeichnungen in fester Reihenfolge — für Filter und Auswahl. */
export const KUNDENMARKEN: readonly Kundenmarke[] = ['interessent', 'kunde', 'kostentraeger', 'inaktiv'];

export const KUNDENMARKENWORT: Readonly<Record<Kundenmarke, string>> = {
  interessent: 'Interessent',
  kunde: 'Kunde',
  kostentraeger: 'Kostenträger',
  inaktiv: 'inaktiv',
};
export type Adresstyp = 'rechnung' | 'liefer' | 'besuch' | 'post';

export type Kundenzeile = {
  readonly id: string;
  readonly kundennummer: string;
  readonly kundenart: Kundenart;
  readonly name: string;
  readonly ort: string | null;
  readonly status: Kundenstatus;
  readonly marke: Kundenmarke;
  readonly gesperrt: boolean;
  readonly betreuer: string | null;
  readonly betreuerName: string | null;
  readonly guete: number | null;
};

/** KUN-04 — die vier Arten, die das Lastenheft nennt. */
export type Kanalart = 'email' | 'telefon' | 'mobil' | 'website';

export const KANALNAME: Record<Kanalart, string> = {
  email: 'E-Mail',
  telefon: 'Telefon',
  mobil: 'Mobil',
  website: 'Website',
};

/**
 * Ein Kommunikationskanal (KUN-04). Er haengt am Kunden oder an einem
 * Ansprechpartner — nie an der Person selbst, weil dieselbe Person bei zwei
 * Kunden zwei Durchwahlen haben kann (KUN-08).
 */
export type Kanal = {
  readonly id: string;
  readonly art: Kanalart;
  readonly wert: string;
  readonly bezeichnung: string | null;
  readonly istPrimaer: boolean;
};

/** Was beim Anlegen eines Kanals gesendet wird — ohne `istPrimaer`. */
export type Kanaleingabe = {
  readonly art: Kanalart;
  readonly wert: string;
  readonly bezeichnung: string | null;
};

/** Eine natuerliche Person, ohne Kontaktdaten: die sind Kanaele (KUN-04). */
export type Person = {
  readonly id: string;
  readonly anrede: string | null;
  readonly titel: string | null;
  readonly vorname: string | null;
  readonly nachname: string;
  readonly geburtsdatum: string | null;
};

/**
 * Die dokumentierte Werbeerlaubnis (LEG-12).
 *
 * `traeger` sagt, WORAN sie hängt: beim Privatkunden an der Person, beim
 * Geschäftskunden am Konto. Die Regel steht in der Datenbank — hier steht
 * nur ihr Ergebnis, damit die Maske es benennen kann.
 */
export type Werbeerlaubnis = {
  readonly stand: 'einwilligung' | 'ausnahme' | 'widerspruch';
  readonly seit: string;
  readonly quelle: string;
  readonly bemerkung: string | null;
  readonly traeger: 'person' | 'kunde';
};

export type Kontakt = {
  readonly id: string;
  readonly person: Person;
  readonly rolle: string;
  readonly funktion: string | null;
  readonly abteilung: string | null;
  readonly rechnungsempfaenger: boolean;
  readonly kanaele: readonly Kanal[];
  readonly ausgeschieden: boolean;
  /**
   * Der in DIESER Firma wirksame Werbestand (LEG-12, seit 0319): Ein
   * Widerspruch der Firma gilt für alle Ansprechpartner, sonst zählt sein
   * Stand hier, dann der der Firma. Sein privater Stand wirkt hier nicht.
   */
  readonly werbestand: Werbeerlaubnis['stand'] | null;
  /** Woher der wirksame Stand kommt. */
  readonly werbestandVon: 'ansprechpartner' | 'firma' | null;
  /** Sein eigener Stand bei dieser Firma (an der Kontaktrolle). */
  readonly eigenerWerbestand: Omit<Werbeerlaubnis, 'traeger'> | null;
};

export type Adresse = {
  readonly id: string;
  readonly typ: Adresstyp;
  readonly zusatz: string | null;
  readonly strasse: string;
  readonly plz: string;
  readonly ort: string;
  readonly land: string;
  readonly standard: boolean;
};

export type Kondition = {
  /** Bearbeitungsstand fuer ERR-13, wie an der Akte. */
  readonly stand: string;
  readonly zahlungsziel: number;
  readonly skontoProzent: string | null;
  readonly skontoTage: number | null;
  readonly rabattgruppe: string | null;
  readonly preisliste: string | null;
  readonly versandart: string | null;
  readonly belegsprache: string;
  readonly ausgabeformat: string;
  readonly zahlungsart: string;
  /** SYS-03: fehlt ganz, wenn das Recht fehlt. */
  readonly kreditlimit?: string | null;
  readonly iban?: string | null;
  readonly bic?: string | null;
  readonly kontoinhaber?: string | null;
};

export type Kunde = {
  readonly id: string;
  readonly kundennummer: string;
  readonly kundenart: Kundenart;
  readonly name: string;
  readonly firmierung: string | null;
  readonly person: Person | null;
  readonly rechtsform: string | null;
  readonly ustidnr: string | null;
  readonly ustidnrStatus: 'ohne' | 'offen' | 'gueltig' | 'ungueltig';
  readonly ustidnrGeprueftAm: string | null;
  readonly leitwegId: string | null;
  readonly status: Kundenstatus;
  readonly marke: Kundenmarke;
  /** PRD-38: die Art des Kostenträgers; `null` heißt kein Kostenträger. */
  readonly kostentraeger: { readonly art: string; readonly bezeichnung: string } | null;
  readonly gesperrt: boolean;
  readonly sperrgrund: string | null;
  readonly betreuer: string | null;
  readonly betreuerName: string | null;
  readonly branche: string | null;
  readonly herkunft: string | null;
  /**
   * BUC-98: die Mitgliedsnummer des Anmelders — nur beim Privatkunden.
   *
   * Sie sagt NICHT, ob die Mitgliedschaft gilt. Das sagt `mitgliedschaft`:
   * die letzte Prüfung zu DIESER Nummer. Wer die Nummer ändert, hat keine
   * geprüfte Mitgliedschaft mehr.
   */
  readonly mitgliedsnummer: string | null;
  readonly mitgliedschaft: {
    readonly ergebnis: 'mitglied' | 'kein_mitglied' | 'nicht_erreichbar';
    readonly geprueftAm: string;
    readonly gegenstelle: string;
  } | null;
  readonly zusammengefuehrtIn: string | null;
  readonly angelegtAm: string;
  readonly stand: string;
  readonly kontakte: readonly Kontakt[];
  readonly adressen: readonly Adresse[];
  /** Die Kanaele des Kunden selbst (KUN-04). */
  readonly kanaele: readonly Kanal[];
  readonly kondition: Kondition | null;
  /** LEG-12: `null` heißt „nichts dokumentiert" — und dann wird nicht geworben. */
  readonly werbeerlaubnis: Werbeerlaubnis | null;
};

export type Dublettenverdacht = {
  readonly id: string;
  readonly kundennummer: string;
  readonly name: string;
  readonly ort: string | null;
  readonly guete: number;
  readonly grund: 'name' | 'ustidnr';
};

export type Listenergebnis = {
  readonly kunden: readonly Kundenzeile[];
  readonly mehrAlsGezeigt: boolean;
};

export async function liste(filter: {
  readonly suche?: string;
  /** Leer oder fehlend: alle Arten. Mehrere gehen als Kommaliste hinaus. */
  readonly kundenarten?: readonly Kundenart[];
  /** PRD-38 (CRM-171): nach der Kennzeichnung; fehlend heißt alle. */
  readonly marke?: Kundenmarke;
  readonly nurEigene?: boolean;
}): Promise<Listenergebnis> {
  const teile = new URLSearchParams();
  if (filter.suche !== undefined && filter.suche !== '') teile.set('suche', filter.suche);
  if (filter.kundenarten !== undefined && filter.kundenarten.length > 0) {
    teile.set('kundenart', filter.kundenarten.join(','));
  }
  if (filter.marke !== undefined) teile.set('status', filter.marke);
  if (filter.nurEigene === true) teile.set('nurEigene', 'true');
  const frage = teile.toString();
  return rufe<Listenergebnis>(`/kunden${frage === '' ? '' : `?${frage}`}`);
}

export async function akte(id: string, signal?: AbortSignal): Promise<Kunde> {
  return rufe<Kunde>(`/kunden/${id}`, signal === undefined ? {} : { signal });
}

/**
 * Der eine Kunde als Listenzeile — für eine Neuanlage, die aus seiner Akte
 * heraus geöffnet wird. Die ungefilterte Liste taugt dafür nicht: Sie endet
 * nach der ersten Seite (`mehrAlsGezeigt`), und ein Kunde dahinter kam nie
 * im Feld an (Durchgang CRM-53: KD-003000 bei 3000 Kunden der Demo).
 *
 * Ort und Güte bleiben leer wie bei der Wahl im Suchfenster: Die Masken
 * zeigen Nummer und Name, die Kundenart holt der Dienst beim Anlegen selbst
 * (ANG-13).
 */
export async function zeile(id: string): Promise<Kundenzeile> {
  const k = await akte(id);
  return {
    id: k.id,
    kundennummer: k.kundennummer,
    kundenart: k.kundenart,
    name: k.name,
    ort: null,
    status: k.status,
    marke: k.marke,
    gesperrt: k.gesperrt,
    betreuer: k.betreuer,
    betreuerName: k.betreuerName,
    guete: null,
  };
}

/** Telefon, Mobil oder E-Mail in der Vorschau; der primaere seiner Art steht vorn (KUN-04). */
export type Vorschaukanal = {
  readonly art: 'telefon' | 'mobil' | 'email';
  readonly wert: string;
  readonly istPrimaer: boolean;
};

export type Vorschaubuchung = {
  readonly buchungsnummer: string;
  readonly status: 'gebucht' | 'umgebucht' | 'storniert' | 'teilstorniert';
  readonly gebuchtAm: string;
  readonly plaetze: number;
  readonly training: string;
  readonly beginn: string;
  readonly ort: string | null;
};

/** Anzahl der Belege und ihre Summe als Dezimaltext. */
export type Belegsumme = {
  readonly anzahl: number;
  readonly summe: string;
};

/**
 * Die Vorschau der Kundenliste (M-10 i. d. F. AeA-45).
 *
 * `null` an einem Block heisst: Der Benutzer hat das Recht nicht. Die
 * Oberflaeche blendet ihn dann aus — sie zeigt keine Null, die wie eine
 * Auskunft aussieht.
 */
export type Kundenvorschau = {
  readonly id: string;
  readonly kundennummer: string;
  readonly kundenart: Kundenart;
  readonly name: string;
  readonly status: Kundenstatus;
  readonly marke: Kundenmarke;
  readonly gesperrt: boolean;
  readonly sperrgrund: string | null;
  readonly betreuerName: string | null;
  readonly angelegtAm: string;
  readonly kontakt: {
    readonly adresse: {
      readonly zusatz: string | null;
      readonly strasse: string;
      readonly plz: string;
      readonly ort: string;
      readonly land: string;
    } | null;
    readonly kanaele: readonly Vorschaukanal[];
    readonly ansprechpartner: {
      readonly name: string;
      readonly funktion: string | null;
      readonly kanaele: readonly Vorschaukanal[];
    } | null;
    readonly weitereAnsprechpartner: number;
  };
  readonly buchungen: {
    readonly anzahl: number;
    readonly anzahl12Monate: number;
    readonly teilnehmende: number;
    readonly teilnehmende12Monate: number;
    readonly ersteAm: string | null;
    readonly letzte: Vorschaubuchung | null;
    readonly naechste: Vorschaubuchung | null;
  } | null;
  readonly gutscheine: { readonly anzahl: number; readonly restwert: string } | null;
  readonly mitgliedsnummer: string | null;
  readonly mitgliedschaft: 'mitglied' | 'kein_mitglied' | 'nicht_erreichbar' | null;
  readonly rabattgruppe: string | null;
  readonly zahlungsziel: number | null;
  readonly werbeerlaubnis: Werbeerlaubnis['stand'] | null;
  readonly kennzahlen: {
    readonly stand: string | null;
    readonly umsatz: {
      readonly laufendesJahr: { readonly von: string; readonly bis: string; readonly netto: string };
      readonly vorjahr: { readonly von: string; readonly bis: string; readonly netto: string };
      /** Die offenen Posten: Anzahl und offener Betrag BRUTTO. */
      readonly offen: Belegsumme;
    } | null;
    /** Offenes Angebotsvolumen NETTO. */
    readonly angebote: Belegsumme | null;
    /** Auftragsbestand NETTO. */
    readonly auftraege: Belegsumme | null;
  };
};

export async function vorschau(id: string): Promise<Kundenvorschau> {
  return rufe<Kundenvorschau>(`/kunden/${id}/vorschau`);
}

/**
 * Die Kontaktkarte am Namen (Auftrag CRM-181): der Kontaktblock der Vorschau
 * ohne Kennzahlen und Buchungen, dazu Marke, Werbestand und — wenn erfragt —
 * der Ansprechpartner selbst.
 */
export type Kontaktkarte = {
  readonly id: string;
  readonly kundennummer: string;
  readonly kundenart: Kundenart;
  readonly name: string;
  readonly marke: Kundenmarke;
  readonly kostentraegerart: string | null;
  readonly gesperrt: boolean;
  readonly werbeerlaubnis: Werbeerlaubnis['stand'] | null;
  readonly kontakt: Kundenvorschau['kontakt'];
  readonly ansprechpartner: {
    readonly id: string;
    readonly name: string;
    readonly rolle: string;
    readonly funktion: string | null;
    readonly abteilung: string | null;
    readonly kanaele: readonly Vorschaukanal[];
    readonly ausgeschieden: boolean;
    readonly werbeerlaubnis: Werbeerlaubnis['stand'] | null;
  } | null;
};

export async function kontaktkarte(id: string, ansprechpartner: string | null): Promise<Kontaktkarte> {
  const frage = ansprechpartner === null ? '' : `?${new URLSearchParams({ ansprechpartner }).toString()}`;
  return rufe<Kontaktkarte>(`/kunden/${id}/kontaktkarte${frage}`);
}

/** KUN-23: waehrend der Eingabe, warnend und nicht blockierend. */
export async function dubletten(
  name: string,
  ustidnr: string | null,
): Promise<readonly Dublettenverdacht[]> {
  const teile = new URLSearchParams({ name });
  if (ustidnr !== null && ustidnr !== '') teile.set('ustidnr', ustidnr);
  const { verdacht } = await rufe<{ verdacht: readonly Dublettenverdacht[] }>(
    `/kunden/dubletten?${teile.toString()}`,
  );
  return verdacht;
}

export type Anlage = {
  readonly kundenart: Kundenart;
  readonly firmierung: string | null;
  readonly person: Omit<Person, 'id'> | null;
  readonly rechtsform: string | null;
  readonly ustidnr: string | null;
  readonly leitwegId: string | null;
  readonly adresse: Omit<Adresse, 'id' | 'standard'> | null;
  readonly kanaele: readonly Kanaleingabe[];
  readonly ansprechpartner: {
    readonly person: Omit<Person, 'id'>;
    readonly funktion: string | null;
    readonly kanaele: readonly Kanaleingabe[];
  } | null;
};

export async function anlegen(
  eingabe: Anlage,
): Promise<{ readonly id: string; readonly kundennummer: string }> {
  return rufe(`/kunden`, { verfahren: 'POST', rumpf: eingabe });
}

/**
 * M-12: feldweise sichern beim Verlassen des Feldes.
 *
 * `stand` geht mit und ist der Grund, warum ERR-13 ueberhaupt erkennbar ist.
 * Gesendet wird NUR das geaenderte Feld — das anwesende Feld ist die Aussage,
 * nicht sein Wert.
 */
/**
 * BUC-98: die hinterlegte Mitgliedsnummer gegen den Anbieter prüfen.
 *
 * POST und nicht GET: Sie fragt eine fremde Schnittstelle und schreibt ins
 * Protokoll. Ein GET, das beides tut, wäre ein GET, das man nicht wiederholen
 * darf.
 */
export async function mitgliedspruefung(kunde: string): Promise<{
  readonly ergebnis: 'mitglied' | 'kein_mitglied' | 'nicht_erreichbar';
  readonly geprueftAm: string;
  readonly gegenstelle: string;
}> {
  return rufe(`/kunden/${kunde}/mitgliedspruefung`, { verfahren: 'POST' });
}

export async function feldSichern(
  id: string,
  feld: string,
  wert: string | null,
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/kunden/${id}`, {
    verfahren: 'PATCH',
    rumpf: { stand, [feld]: wert },
  });
}

/* ── M-13 Ansprechpartner (KUN-08) ───────────────────────────────────────── */

export async function kontaktAnlegen(
  kunde: string,
  eingabe: {
    readonly person: Omit<Person, 'id'>;
    readonly rolle: string;
    readonly funktion: string | null;
    readonly abteilung: string | null;
    readonly rechnungsempfaenger: boolean;
    readonly kanaele: readonly Kanaleingabe[];
  },
): Promise<{ readonly id: string }> {
  return rufe(`/kunden/${kunde}/kontakte`, { verfahren: 'POST', rumpf: eingabe });
}

/* ── Kommunikationskanaele (KUN-04) ──────────────────────────────────────── */

export async function kanalAnlegen(
  kunde: string,
  eingabe: Kanaleingabe & { readonly kontaktrolle: string | null },
): Promise<{ readonly id: string }> {
  return rufe(`/kunden/${kunde}/kanaele`, { verfahren: 'POST', rumpf: eingabe });
}

export async function kanalPrimaerSetzen(kunde: string, kanal: string): Promise<void> {
  await rufe(`/kunden/${kunde}/kanaele/${kanal}/primaer`, { verfahren: 'PATCH' });
}

export async function kanalLoeschen(kunde: string, kanal: string): Promise<void> {
  await rufe(`/kunden/${kunde}/kanaele/${kanal}`, { verfahren: 'DELETE' });
}

/**
 * Der Ansprechpartner scheidet aus. Er wird NICHT geloescht — ein Angebot aus
 * dem Vorjahr traegt seinen Namen (DAT-25).
 */
export async function kontaktBeenden(kunde: string, kontakt: string): Promise<void> {
  return rufe(`/kunden/${kunde}/kontakte/${kontakt}`, { verfahren: 'DELETE' });
}

/* ── M-14 Konditionen (KUN-10, SYS-03) ───────────────────────────────────── */

export type KonditionAenderung = {
  readonly zahlungsziel?: number;
  readonly rabattgruppe?: string | null;
  readonly versandart?: string | null;
  readonly belegsprache?: string;
  readonly ausgabeformat?: string;
  readonly zahlungsart?: string;
  readonly kreditlimit?: string | null;
  readonly iban?: string | null;
  readonly bic?: string | null;
  readonly kontoinhaber?: string | null;
};

export async function konditionSichern(
  kunde: string,
  felder: KonditionAenderung,
  stand: string,
): Promise<void> {
  return rufe(`/kunden/${kunde}/konditionen`, {
    verfahren: 'PATCH',
    rumpf: { stand, ...felder },
  });
}

/* ── M-18 Dubletten zusammenfuehren (KUN-25, SYS-16) ─────────────────────── */

export type Uebernahme = {
  readonly firmierung?: string | null;
  readonly rechtsform?: string | null;
  readonly ustidnr?: string | null;
  readonly branche?: string | null;
  readonly herkunft?: string | null;
};

/** `ziel` bleibt bestehen, `quelle` wird umgeleitet. */
export async function zusammenfuehren(
  ziel: string,
  quelle: string,
  uebernehmen: Uebernahme,
): Promise<{ readonly ruecknahmeBis: string }> {
  return rufe(`/kunden/${ziel}/zusammenfuehren`, {
    verfahren: 'POST',
    rumpf: { quelle, uebernehmen },
  });
}

export async function zusammenfuehrungRuecknehmen(quelle: string): Promise<void> {
  return rufe(`/kunden/${quelle}/zusammenfuehrung`, { verfahren: 'DELETE' });
}
